import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ChartBarIcon,
    BoltIcon,
    Cog6ToothIcon,
    DevicePhoneMobileIcon,
    CurrencyDollarIcon,
    UserGroupIcon,
    ShieldCheckIcon,
    AcademicCapIcon,
    GlobeAltIcon,
    PuzzlePieceIcon,
    MagnifyingGlassIcon,
    InboxArrowDownIcon,
    ChatBubbleLeftRightIcon,
    LightBulbIcon,
    PresentationChartBarIcon,
    RocketLaunchIcon,
    WrenchIcon,
    ClipboardDocumentCheckIcon,
    CpuChipIcon,
    DocumentTextIcon,
} from "@heroicons/react/24/outline";
import {
    db,
    collection,
    query,
    where,
    getDocs,
    addDoc
} from "../firebase"; // Ajusta la ruta
/* ------------------------------------------------------------------
 * Landing page: busca el Lead en Firestore y muestra sus beneficios.
 * URL esperada: /:clientSlug  (clientSlug === doc.clientNameUrl)
 * Si no existe doc → muestra placeholders con "La empresa".
 * Animaciones: Framer‑motion fade/slide.
 * ------------------------------------------------------------------ */



export default function XerooClientLanding() {


    /* ---------------- Placeholder sentences ---------------- */
    const placeholderSentences = [
        "La empresa puede automatizar la atención al cliente 24/7",
        "La empresa puede predecir la demanda y optimizar inventarios",
        "La empresa puede detectar fraudes en transacciones en tiempo real",
        "La empresa puede segmentar clientes mediante análisis de comportamiento",
        "La empresa puede personalizar campañas de marketing automáticamente",
        "La empresa puede optimizar precios dinámicamente",
        "La empresa puede generar reportes financieros inteligentes al instante",
        "La empresa puede mejorar la calidad con visión por computadora",
        "La empresa puede acelerar I+D con simulaciones impulsadas por IA",
        "La empresa puede reducir tiempos de mantenimiento predictivo",
        "La empresa puede optimizar rutas logísticas y entregas",
        "La empresa puede clasificar y responder correos automáticamente",
        "La empresa puede analizar sentimiento en redes sociales",
        "La empresa puede generar contenido y copys en segundos",
        "La empresa puede detectar oportunidades de upselling en tiempo real",
        "La empresa puede crear chatbots internos para soporte técnico",
        "La empresa puede mejorar la seguridad mediante monitoreo inteligente",
        "La empresa puede reducir costos energéticos con IA predictiva"
    ];


    const mapToBenefits = (arr) => arr.slice(0, 18).map((t, i) => ({ text: t, icon: iconList[i % iconList.length] }));


    const primary = "#5860D5";

    /* Heroicons list */
    const iconList = [
        ChatBubbleLeftRightIcon,
        ChartBarIcon,
        ShieldCheckIcon,
        UserGroupIcon,
        BoltIcon,
        CurrencyDollarIcon,
        ClipboardDocumentCheckIcon,
        MagnifyingGlassIcon,
        LightBulbIcon,
        WrenchIcon,
        GlobeAltIcon,
        InboxArrowDownIcon,
        DevicePhoneMobileIcon,
        DocumentTextIcon,
        PresentationChartBarIcon,
        CpuChipIcon,
        ShieldCheckIcon,
        RocketLaunchIcon,
        PuzzlePieceIcon,
        AcademicCapIcon,
    ];


    /* slug from URL */
    const { clientSlug = "" } = useParams();

    /* ---------------- State ---------------- */
    const [loading, setLoading] = useState(true);
    const [selectedBenefits, setSelectedBenefits] = useState([]);
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [sent, setSent] = useState(false);
    const [clientName, setClientName] = useState("las empresas");
    const [clientLogo, setClientLogo] = useState("");
    const [benefits, setBenefits] = useState(mapToBenefits(placeholderSentences));

    /* ---------------- Fetch lead ---------------- */
    useEffect(() => {
        const fetchLead = async () => {
            try {
                const q = query(collection(db, "Leads"), where("clientNameUrl", "==", clientSlug));
                const snap = await getDocs(q);
                if (!snap.empty) {
                    const data = snap.docs[0].data();
                    setClientName(data.clientName);
                    setClientLogo(data.clientLogo || "");
                    if (Array.isArray(data.clientBenefits) && data.clientBenefits.length) {
                        setBenefits(mapToBenefits(data.clientBenefits));
                    }
                }
            } catch (e) {
                console.error("Firestore fetch error", e);
            }
            setLoading(false);
        };
        fetchLead();
    }, [clientSlug]);


    /* ---------------- Handlers ---------------- */
    const toggleBenefit = (b) => {
        setSelectedBenefits((prev) => {
            const exists = prev.includes(b);
            const next = exists ? prev.filter((x) => x !== b) : [...prev, b];
            setForm((f) => ({ ...f, message: next.join("\n") }));
            return next;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await addDoc(collection(db, "LeadsInteresados"), {
                name: form.name,
                email: form.email,
                message: form.message,
                clientSlug,
                createdAt: Date.now()
            });
            setSent(true);
        } catch (err) {
            console.error("addDoc", err);
        }
    };

    /* ---------------- UI ---------------- */
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <motion.div
                    className="h-12 w-12 rounded-full border-4 border-[#5860D5] border-t-transparent"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, ease: "linear", duration: 1 }}
                />
            </div>
        );
    }

    /* animations variants */
    const fadeSlide = {
        hidden: { opacity: 0, y: 20 },
        visible: (i = 0) => ({
            opacity: 1,
            y: 0,
            transition: { delay: 0.1 * i, duration: 0.5 },
        }),
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            {/* Navbar */}
            <header className="flex items-center justify-between px-6 py-4 bg-white shadow-md">
                <img src="/xeroo-logo-violet.png" alt="Xeroo Logo" className="h-10" />
                <nav className="hidden md:flex space-x-6 font-medium">
                    <a href="#benefits" className="hover:text-gray-700">Beneficios</a>
                    <a href="#contact" className="hover:text-gray-700">Agendar reunión</a>
                </nav>
                {clientLogo && <img src={clientLogo} alt="Client Logo" className="h-10" />}
            </header>

            {/* Hero */}
            <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col items-center text-center px-4 py-16 bg-white"
            >
                <h1 className="text-4xl md:text-5xl font-extrabold max-w-3xl leading-tight" style={{ color: primary }}>
                    Beneficios concretos para <span className="text-[#5860D5]">{clientName}</span>
                </h1>
                <p className="mt-4 text-lg text-gray-600 max-w-xl">
                    Soluciones de Inteligencia Artificial diseñadas para impulsar el crecimiento y la eficiencia en tu negocio.
                </p>
            </motion.section>

            {/* Benefits */}
            <section id="benefits" className="py-12 bg-gray-50">
                <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {benefits.map(({ text, icon: Icon }, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            onClick={() => toggleBenefit(text)}
                            className={`cursor-pointer select-none bg-white rounded-2xl p-6 shadow hover:shadow-lg flex items-start space-x-4 ${selectedBenefits.includes(text) ? "ring-2 ring-[#5860D5]" : ""}`}
                        >
                            <div className="p-3 rounded-full" style={{ backgroundColor: primary + "1A" }}>
                                <Icon className="h-6 w-6" style={{ color: primary }} />
                            </div>
                            <p className="text-gray-700 leading-snug">{text}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Contact */}
            <motion.section
                id="contact"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="py-16 bg-white"
            >
                <div className="max-w-xl mx-auto px-6">
                    <h2 className="text-2xl font-bold text-center" style={{ color: primary }}>
                        Agendá una reunión con nosotros
                    </h2>
                    {sent ? (
                        <p className="mt-8 text-center font-semibold text-[#2d7f2e]">¡Mensaje enviado! Nos pondremos en contacto.</p>
                    ) : (
                        <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
                            <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Tu nombre" className="w-full border border-gray-300 rounded-lg px-4 py-2" required />
                            <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="tu@correo.com" className="w-full border border-gray-300 rounded-lg px-4 py-2" required />
                            <textarea name="message" value={form.message} onChange={handleChange} rows="4" placeholder="Contanos tu necesidad" className="w-full border border-gray-300 rounded-lg px-4 py-2" />
                            <button type="submit" className="w-full py-3 font-semibold text-white rounded-xl" style={{ backgroundColor: primary }}>
                                Enviar
                            </button>
                        </form>
                    )}
                </div>
            </motion.section>

            {/* Footer */}
            <footer className="py-6 text-center text-sm text-gray-500 bg-gray-50">
                © {new Date().getFullYear()} Xeroo. Todos los derechos reservados.
            </footer>
        </div>
    );
}